import { useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

interface ScrollPosition {
  x: number
  y: number
}

/** Новая карточка открывается сверху; «Назад» возвращает к месту в списке. */
export function RouteScrollRestoration() {
  const location = useLocation()
  const navigationType = useNavigationType()
  const positions = useRef(new Map<string, ScrollPosition>())
  const previousPathname = useRef<string | null>(null)

  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    return () => { window.history.scrollRestoration = previous }
  }, [])

  useLayoutEffect(() => {
    const savedPositions = positions.current
    const saved = navigationType === 'POP' ? savedPositions.get(location.key) : undefined
    const samePage = previousPathname.current === location.pathname
    previousPathname.current = location.pathname
    const target = saved ?? (samePage ? { x: window.scrollX, y: window.scrollY } : { x: 0, y: 0 })
    let lastPosition = target
    let waiting = true
    let observer: ResizeObserver | null = null

    function stopWaiting() {
      waiting = false
      observer?.disconnect()
      lastPosition = { x: window.scrollX, y: window.scrollY }
      window.removeEventListener('wheel', stopWaiting)
      window.removeEventListener('touchstart', stopWaiting)
      window.removeEventListener('pointerdown', stopWaiting)
      window.removeEventListener('keydown', stopWaiting)
    }

    function restore() {
      if (!waiting) return
      window.scrollTo({ left: target.x, top: target.y, behavior: 'instant' })
      const scrollHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
      if (target.y <= Math.max(0, scrollHeight - window.innerHeight)) stopWaiting()
    }

    function rememberPosition() {
      if (!waiting) lastPosition = { x: window.scrollX, y: window.scrollY }
    }

    restore()
    // Список может сначала показать загрузку. Восстанавливаем место, когда появятся задания.
    if (waiting && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(restore)
      observer.observe(document.body)
      window.addEventListener('wheel', stopWaiting, { passive: true })
      window.addEventListener('touchstart', stopWaiting, { passive: true })
      window.addEventListener('pointerdown', stopWaiting, { passive: true })
      window.addEventListener('keydown', stopWaiting)
    } else if (waiting) stopWaiting()
    window.addEventListener('scroll', rememberPosition, { passive: true })

    return () => {
      savedPositions.set(location.key, lastPosition)
      stopWaiting()
      window.removeEventListener('scroll', rememberPosition)
    }
  }, [location.key, location.pathname, navigationType])

  return null
}
